import React, { useMemo, useState } from "react";
import Client from "../../models/client";
import useSWR from "swr";
import { copyEmailNotice, deleteEmailNotice, fetchComingCouponCvList, fetchEmailNoticesPreference, fetchEnduserEventsComingCouponCvList } from "../api";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { ActionIcon, Anchor, Badge, Box, Button, Center, Group, Loader, Table, Text, ThemeIcon, Title, Tooltip } from "@mantine/core";
import { CopyIcon, PencilSimpleIcon, PlusIcon, TicketIcon, TrashIcon } from "@phosphor-icons/react";
import EmailNoticesPreferenceModal from "../components/EmailNoticesPreferenceModal";
import ComingCouponEmailNoticeFormModal from "../components/ComingCouponEmailNoticeFormModal";
import { confirmCopy, confirmDelete } from "../utils/confirm";
import { recordTypeLabel } from "../components/EnduserEventForm";

interface Props {
  client: Client
}

type EmailNoticeRow = {
  id: number
  enduser_event_id: number
  is_active: boolean
  subject: string
  to_type: string
}

const ComingCouponEmailNoticeContainer: React.FC<Props> = ({ client }) => {
  const {
    data: emailNotices,
    mutate: mutateEmailNotices,
  } = useSWR<EmailNoticeRow[]>(
    `/admin/clients/${client.id}/email_notices/coming_coupon_cv_list`,
    () => fetchComingCouponCvList(client.id),
  )

  const { error: errorPref, mutate: mutatePref } = useSWR(
    `/admin/clients/${client.id}/email_notices_preference`,
    () => fetchEmailNoticesPreference(client.id),
  )

  const { data: eventOptions } = useSWR(
    `/admin/clients/${client.id}/enduser_events/coming_coupon_cv_list`,
    () => fetchEnduserEventsComingCouponCvList(client.id),
  )

  const eventMap = useMemo(() => {
    const map = new Map<number, { name: string; record_type: string }>()
      ; (eventOptions ?? []).forEach(
        (e: { id: number; name: string; record_type: string }) =>
          map.set(e.id, { name: e.name, record_type: e.record_type }),
      )
    return map
  }, [eventOptions])

  const [openedForm, formModal] = useDisclosure(false)
  const [openedPref, prefModal] = useDisclosure(false)
  const [editingId, setEditingId] = useState<number | null>(null)

  const openCreate = () => {
    setEditingId(null)
    formModal.open()
  }

  const openEdit = (row: EmailNoticeRow) => {
    setEditingId(row.id)
    formModal.open()
  }

  const handleDelete = async (row: EmailNoticeRow) => {
    if (!confirmDelete('メール通知')) return
    try {
      await deleteEmailNotice(client.id, row.id)
      await mutateEmailNotices()
      notifications.show({ message: 'メール通知を削除しました', color: 'teal' })
    } catch (e) {
      console.error('メール通知の削除に失敗しました', e)
      notifications.show({ message: '削除に失敗しました', color: 'red' })
    }
  }

  const handleCopy = async (row: EmailNoticeRow) => {
    if (!confirmCopy('メール通知')) return
    try {
      await copyEmailNotice(client.id, row.id)
      await mutateEmailNotices()
      notifications.show({ message: 'メール通知をコピーしました', color: 'teal' })
    } catch (e) {
      console.error('コピーに失敗しました', e)
      notifications.show({ message: 'コピーに失敗しました', color: 'red' })
    }
  }

  if (!emailNotices) {
    return (
      <Center mih={200}>
        <Loader />
      </Center>
    )
  }

  return (
    <Box>
      <Group gap={12} mb={24}>
        <ThemeIcon variant="transparent" color="#4BC1C7">
          <TicketIcon weight="fill" size={36} />
        </ThemeIcon>
        <Title size={24} fw={600} c="#000000CC">
          メール通知設定
        </Title>
      </Group>

      <Text fz={14} c="#000000CC">
        通知設定では、通知するメールの内容を設定します。<br />
        ※通知設定の方法については、
        <Anchor
          href="https://www.creativehope.co.jp/invyknowledge/notification/02"
          target="_blank"
          rel="noopener noreferrer"
          fz={14}
          underline='always'
        >
          マニュアル（invyご利用ガイド）
        </Anchor>
        をご確認ください。
      </Text>

      <Button
        variant="filled"
        radius="xl"
        leftSection={<PlusIcon size={16} weight="bold" />}
        onClick={openCreate}
        mt={40}
      >
        新規作成
      </Button>

      <Box style={{ border: '1px solid #0000001A', borderRadius: 8, overflow: 'hidden', marginTop: 16 }}>
        <Table withColumnBorders styles={{ th: { backgroundColor: '#00000008' } }}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th w={160}>イベントID</Table.Th>
              <Table.Th>イベント名</Table.Th>
              <Table.Th>タイプ</Table.Th>
              <Table.Th w={110}>有効</Table.Th>
              <Table.Th>件名</Table.Th>
              <Table.Th w={160} ta="center">プレビュー</Table.Th>
              <Table.Th w={80} ta="center">編集</Table.Th>
              <Table.Th w={80} ta="center">コピー</Table.Th>
              <Table.Th w={80} ta="center">削除</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {emailNotices.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={9}>
                  <Text c="#00000099" ta="center" py="md">
                    メール通知が登録されていません
                  </Text>
                </Table.Td>
              </Table.Tr>
            ) : (
              emailNotices.map((item) => {
                const ev = eventMap.get((item.enduser_event_id))
                return (
                  <Table.Tr key={item.id}>
                    <Table.Td>{item.enduser_event_id}</Table.Td>
                    <Table.Td>{ev?.name ?? '-'}</Table.Td>
                    <Table.Td>{ev ? recordTypeLabel(ev.record_type) : '-'}</Table.Td>
                    <Table.Td>
                      <Badge color={item.is_active ? 'invy' : 'gray'} variant="light">
                        {item.is_active ? '有効' : '無効'}
                      </Badge>
                    </Table.Td>
                    <Table.Td>{item.subject}</Table.Td>
                    <Table.Td ta="center">
                      <Anchor
                        href={`/admin/api/clients/${client.id}/email_notices/${item.id}/${item.to_type === 'guest' ? 'preview_guest_coupon' : 'preview_inviter_coupon'}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        fz={13}
                      >
                        プレビュー
                      </Anchor>
                    </Table.Td>
                    <Table.Td ta="center">
                      <Tooltip label="編集">
                        <ActionIcon variant="subtle" color="gray" onClick={() => openEdit(item)}>
                          <PencilSimpleIcon size={18} />
                        </ActionIcon>
                      </Tooltip>
                    </Table.Td>
                    <Table.Td ta="center">
                      <Tooltip label="コピー">
                        <ActionIcon variant="subtle" color="gray" onClick={() => handleCopy(item)}>
                          <CopyIcon size={18} />
                        </ActionIcon>
                      </Tooltip>
                    </Table.Td>
                    <Table.Td ta="center">
                      <Tooltip label="削除">
                        <ActionIcon variant="subtle" color="red" onClick={() => handleDelete(item)}>
                          <TrashIcon size={18} />
                        </ActionIcon>
                      </Tooltip>
                    </Table.Td>
                  </Table.Tr>
                )
              })
            )}
          </Table.Tbody>
        </Table>
      </Box>

      <Group gap={12} align="center" mt={40} mb={16}>
        <Title order={2} size={18} fw={600}>
          フッター設定
        </Title>
        <Badge color={errorPref ? 'gray' : 'teal'} variant="light">
          {errorPref ? '未設定' : '設定済み'}
        </Badge>
      </Group>
      <Button
        variant="outline"
        radius="xl"
        onClick={prefModal.open}
      >
        編集
      </Button>

      <ComingCouponEmailNoticeFormModal
        opened={openedForm}
        onClose={formModal.close}
        client={client}
        noticeId={editingId}
        eventOptions={eventOptions ?? []}
        onSaved={mutateEmailNotices}
      />
      <EmailNoticesPreferenceModal
        opened={openedPref}
        onClose={prefModal.close}
        client={client}
        onSaved={mutatePref}
      />
    </Box>
  )
}

export default ComingCouponEmailNoticeContainer
